// Realtime Database Signaling Service for WebRTC
import { 
  ref, 
  set, 
  onValue, 
  off, 
  push, 
  remove,
  update,
  get
} from 'firebase/database';
import { doc, updateDoc } from 'firebase/firestore';
import { realtimeDb, db } from './config';

/**
 * Realtime Database service for WebRTC signaling
 * Handles peer discovery and signaling (offers, answers, ICE candidates)
 */
export const signalingService = {
  /**
   * Join a live class room
   * @param {string} classId - Class ID
   * @param {string} userId - User ID
   * @param {string} userName - User name
   * @param {boolean} isHost - Whether user is host
   * @returns {Promise<Object>} - Join result
   */
  async joinRoom(classId, userId, userName, isHost = false) {
    // Check if Realtime Database is initialized
    if (!realtimeDb) {
      console.error('Realtime Database is not initialized');
      throw new Error('Realtime Database is not initialized. Please check Firebase configuration and ensure databaseURL is correct.');
    }

    try {
      console.log('Attempting to join room with Realtime DB:', realtimeDb);
      const roomRef = ref(realtimeDb, `liveClasses/${classId}`);
      const participantsRef = ref(realtimeDb, `liveClasses/${classId}/participants/${userId}`);
      
      // Try to create/update room first (simpler approach)
      try {
        // Try to set participant data directly - this will work if DB is accessible
        const participantData = {
          userId,
          userName: userName || 'Anonymous',
          isHost,
          joinedAt: Date.now(),
          status: 'connected',
          peerId: userId,
          isMuted: false,
          isVideoOff: false
        };
        
        console.log(`Adding participant to room ${classId}:`, participantData);
        console.log(`Participant ref path:`, participantsRef.toString());
        
        try {
          await set(participantsRef, participantData);
          console.log(`Successfully added participant ${userId} to room ${classId}`);
        } catch (setError) {
          console.error('Error setting participant data:', setError);
          console.error('Error details:', {
            code: setError.code,
            message: setError.message,
            name: setError.name
          });
          throw setError;
        }

        // Update room info if host (use update to not overwrite existing data)
        if (isHost) {
          await update(roomRef, {
            classId,
            hostId: userId,
            createdAt: Date.now(),
            status: 'active'
          });
        } else {
          // Just ensure room exists (use update to not overwrite)
          await update(roomRef, {
            classId,
            status: 'active'
          });
        }

        // Update participant count in Firestore (async, don't wait)
        this.updateParticipantCountInFirestore(classId).catch(err => {
          console.warn('Failed to update Firestore participant count:', err);
        });
      } catch (writeError) {
        console.error('Write error details:', writeError);
        console.error('Error code:', writeError.code);
        console.error('Error name:', writeError.name);
        console.error('Error message:', writeError.message);
        console.error('Full error object:', writeError);
        
        // Check for specific errors - be more precise
        const errorCode = writeError.code || writeError.name || '';
        const errorMessage = writeError.message || String(writeError) || '';
        
        // Most common issue: Permission denied
        if (errorCode === 'PERMISSION_DENIED' || 
            errorCode === 'permission-denied' || 
            errorCode === 'PERMISSION-DENIED' ||
            errorMessage.toLowerCase().includes('permission') ||
            errorMessage.toLowerCase().includes('denied')) {
          throw new Error('Permission denied. Please check Firebase Realtime Database rules. Go to Firebase Console > Realtime Database > Rules and set: { "rules": { "liveClasses": { ".read": "auth != null", ".write": "auth != null" } } }');
        } 
        // Database unavailable
        else if (errorCode === 'UNAVAILABLE' || 
                 errorCode === 'unavailable' || 
                 errorMessage.toLowerCase().includes('unavailable')) {
          throw new Error('Realtime Database is unavailable. Please check your internet connection.');
        } 
        // Network error
        else if (errorCode === 'NETWORK_ERROR' || 
                 errorCode === 'network-error' || 
                 errorMessage.toLowerCase().includes('network')) {
          throw new Error('Network error. Please check your internet connection.');
        } 
        // Show actual error message (don't say "not enabled" unless it really says that)
        else {
          throw new Error(`Failed to join room: ${errorMessage || errorCode || 'Unknown error'}`);
        }
      }

      return { success: true, roomId: classId };
    } catch (error) {
      console.error('Error joining room:', error);
      // Provide more helpful error message
      if (error.message) {
        throw error;
      }
      throw new Error('Failed to join room. Please check Firebase Realtime Database configuration.');
    }
  },

  /**
   * Leave a live class room
   * @param {string} classId - Class ID
   * @param {string} userId - User ID
   */
  async leaveRoom(classId, userId) {
    try {
      console.log(`[signalingService] Cleaning up all data for user ${userId} in class ${classId}`);
      
      // Remove participant data
      const participantRef = ref(realtimeDb, `liveClasses/${classId}/participants/${userId}`);
      await remove(participantRef);
      console.log(`[signalingService] Removed participant data for ${userId}`);
      
      // Clean up all signaling data where user is involved (as sender or receiver)
      try {
        const signalingRef = ref(realtimeDb, `liveClasses/${classId}/signaling`);
        const signalingSnapshot = await get(signalingRef);
        
        if (signalingSnapshot.exists()) {
          const signalingData = signalingSnapshot.val();
          const updates = {};
          let removedCount = 0;
          
          // Find all signaling messages involving this user
          Object.keys(signalingData || {}).forEach(key => {
            // Signaling keys are in format: fromUserId_toUserId
            const parts = key.split('_');
            if (parts.length >= 2) {
              const fromUserId = parts[0];
              const toUserId = parts.slice(1).join('_'); // Handle case where userId contains underscores
              
              if (fromUserId === userId || toUserId === userId) {
                updates[key] = null; // Mark for deletion
                removedCount++;
              }
            }
          });
          
          // Remove all signaling messages involving this user
          if (Object.keys(updates).length > 0) {
            await update(signalingRef, updates);
            console.log(`[signalingService] Removed ${removedCount} signaling messages for ${userId}`);
          }
        }
      } catch (signalingError) {
        console.warn('[signalingService] Error cleaning up signaling data (non-critical):', signalingError);
      }
      
      // Also remove the user's signaling directory if it exists (legacy cleanup)
      try {
        const userSignalingRef = ref(realtimeDb, `liveClasses/${classId}/signaling/${userId}`);
        await remove(userSignalingRef);
      } catch (error) {
        // Ignore if it doesn't exist - this is expected for new structure
      }

      // Update participant count in Firestore (async, don't wait)
      this.updateParticipantCountInFirestore(classId).catch(err => {
        console.warn('Failed to update Firestore participant count:', err);
      });
      
      console.log(`[signalingService] ✅ Complete cleanup finished for ${userId}`);
    } catch (error) {
      console.error('[signalingService] Error leaving room:', error);
      throw error;
    }
  },

  /**
   * Subscribe to participants in a room
   * @param {string} classId - Class ID
   * @param {Function} callback - Callback with participants array
   * @returns {Function} - Unsubscribe function
   */
  subscribeToParticipants(classId, callback) {
    const participantsRef = ref(realtimeDb, `liveClasses/${classId}/participants`);
    
    console.log(`📡 Subscribing to participants for class: ${classId}`);
    console.log(`📡 Participants ref path: liveClasses/${classId}/participants`);
    
    // Use onValue which fires immediately and on every change
    const unsubscribe = onValue(
      participantsRef, 
      async (snapshot) => {
        console.log('📊 Participants snapshot received:', {
          exists: snapshot.exists(),
          hasChildren: snapshot.hasChildren(),
          numChildren: snapshot.numChildren || (snapshot.exists() ? Object.keys(snapshot.val() || {}).length : 0),
          timestamp: Date.now()
        });
        
        const participants = [];
        if (snapshot.exists()) {
          const data = snapshot.val();
          console.log('📊 Participants raw data:', data);
          const keys = Object.keys(data || {});
          console.log('📊 Participants keys:', keys);
          console.log('📊 Number of keys:', keys.length);
          
          // Use forEach to iterate through all children
          snapshot.forEach((childSnapshot) => {
            const participantData = childSnapshot.val();
            const key = childSnapshot.key;
            console.log(`📊 Processing participant key: ${key}`, participantData);
            
            if (participantData) {
              const participant = {
                id: key,
                userId: participantData.userId || key,
                userName: participantData.userName || 'Anonymous',
                isHost: participantData.isHost || false,
                status: participantData.status || 'connected',
                isMuted: participantData.isMuted || false,
                isVideoOff: participantData.isVideoOff || false,
                ...participantData
              };
              participants.push(participant);
              console.log(`✅ Added participant: ${participant.userName} (${participant.userId})`);
            }
          });
        }
        
        console.log(`✅ Total participants found: ${participants.length}`);
        console.log(`✅ Participant list:`, participants.map(p => ({ 
          userId: p.userId, 
          userName: p.userName 
        })));
        
        // Always call callback with current participants list
        console.log('📞 Calling participants callback with', participants.length, 'participants');
        try {
          callback(participants);
          console.log('✅ Participants callback executed successfully');
        } catch (callbackError) {
          console.error('❌ Error in participants callback:', callbackError);
        }

        // Update participant count in Firestore whenever participants change (async, don't wait)
        this.updateParticipantCountInFirestore(classId).catch(err => {
          console.warn('Failed to update Firestore participant count:', err);
        });
      }, 
      (error) => {
        console.error('❌ Error in participants subscription:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          name: error.name,
          stack: error.stack
        });
        // Call callback with empty array on error
        callback([]);
      },
      {
        // Only fire on value changes, not on initial load (we want both)
        onlyOnce: false
      }
    );

    return () => {
      console.log('🔌 Unsubscribing from participants');
      try {
        off(participantsRef);
        unsubscribe();
        console.log('✅ Successfully unsubscribed from participants');
      } catch (err) {
        console.error('Error unsubscribing:', err);
      }
    };
  },

  /**
   * Send signaling data (offer, answer, or ICE candidate)
   * @param {string} classId - Class ID
   * @param {string} fromUserId - Sender user ID
   * @param {string} toUserId - Receiver user ID (null for broadcast)
   * @param {string} type - 'offer', 'answer', or 'ice-candidate'
   * @param {Object} data - Signaling data (sdp or candidate)
   */
  async sendSignaling(classId, fromUserId, toUserId, type, data) {
    try {
      const signalingKey = toUserId ? `${fromUserId}_${toUserId}` : `${fromUserId}_broadcast`;
      const signalingRef = ref(realtimeDb, `liveClasses/${classId}/signaling/${signalingKey}`);
      
      await set(signalingRef, {
        fromUserId,
        toUserId: toUserId || null,
        type,
        ...data,
        timestamp: Date.now()
      });
    } catch (error) {
      console.error('Error sending signaling:', error);
      throw error;
    }
  },

  /**
   * Subscribe to signaling messages
   * @param {string} classId - Class ID
   * @param {string} userId - Current user ID
   * @param {Function} callback - Callback with signaling message
   * @returns {Function} - Unsubscribe function
   */
  subscribeToSignaling(classId, userId, callback) {
    const signalingRef = ref(realtimeDb, `liveClasses/${classId}/signaling`);
    
    console.log(`📡 [Signaling] Subscribing for class ${classId}, user ${userId}`);
    
    // Track processed messages to prevent duplicates
    const processedMessages = new Set();
    
    const unsubscribe = onValue(signalingRef, (snapshot) => {
      console.log('📥 [Signaling] Snapshot received:', snapshot.exists());
      if (!snapshot.exists()) {
        console.log('📥 [Signaling] No signaling data in snapshot');
        return;
      }
      
      snapshot.forEach((childSnapshot) => {
        const data = childSnapshot.val();
        const messageKey = childSnapshot.key;
        
        // Skip if already processed
        if (processedMessages.has(messageKey)) {
          return;
        }
        
        console.log('📥 [Signaling] Processing message:', {
          key: messageKey,
          from: data.fromUserId,
          to: data.toUserId,
          type: data.type,
          timestamp: data.timestamp
        });
        
        // Only process messages to this user (not from self)
        if (data.fromUserId !== userId && data.toUserId === userId) {
          console.log('✅ [Signaling] Delivering message to callback:', messageKey);
          processedMessages.add(messageKey);
          
          // Clean up old processed messages (keep last 100)
          if (processedMessages.size > 100) {
            const firstKey = Array.from(processedMessages)[0];
            processedMessages.delete(firstKey);
          }
          
          callback({
            id: messageKey,
            ...data
          });
          
          // Auto-cleanup: Remove message after delivery (prevents re-processing)
          setTimeout(() => {
            const messageRef = ref(realtimeDb, `liveClasses/${classId}/signaling/${messageKey}`);
            remove(messageRef).catch(err => {
              console.warn('Failed to cleanup signaling message:', err);
            });
          }, 5000); // 5 second delay to ensure both peers receive it
        } else {
          console.log('⏭️ [Signaling] Skipping message (not for this user)');
        }
      });
    }, (error) => {
      console.error('❌ [Signaling] Subscription error:', error);
    });

    return () => {
      console.log('🔌 [Signaling] Unsubscribing');
      off(signalingRef);
      unsubscribe();
      processedMessages.clear();
    };
  },

  /**
   * Send chat message
   * @param {string} classId - Class ID
   * @param {string} userId - User ID
   * @param {string} userName - User name
   * @param {string} message - Message text
   */
  async sendChatMessage(classId, userId, userName, message) {
    try {
      const chatRef = ref(realtimeDb, `liveClasses/${classId}/chat`);
      await push(chatRef, {
        userId,
        userName: userName || 'Anonymous',
        message,
        timestamp: Date.now()
      });
    } catch (error) {
      console.error('Error sending chat message:', error);
      throw error;
    }
  },

  /**
   * Subscribe to chat messages
   * @param {string} classId - Class ID
   * @param {Function} callback - Callback with messages array
   * @returns {Function} - Unsubscribe function
   */
  subscribeToChat(classId, callback) {
    const chatRef = ref(realtimeDb, `liveClasses/${classId}/chat`);
    
    const unsubscribe = onValue(chatRef, (snapshot) => {
      const messages = [];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          messages.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        // Sort by timestamp
        messages.sort((a, b) => {
          const aTime = a.timestamp || 0;
          const bTime = b.timestamp || 0;
          return aTime - bTime;
        });
      }
      callback(messages);
    });

    return () => {
      off(chatRef);
      unsubscribe();
    };
  },

  /**
   * Update participant status (muted, video off, etc.)
   * @param {string} classId - Class ID
   * @param {string} userId - User ID
   * @param {Object} updates - Status updates
   */
  async updateParticipantStatus(classId, userId, updates) {
    try {
      const participantRef = ref(realtimeDb, `liveClasses/${classId}/participants/${userId}`);
      await update(participantRef, updates);
    } catch (error) {
      console.error('Error updating participant status:', error);
      throw error;
    }
  },

  /**
   * Update participant count in Firestore based on Realtime DB
   * @param {string} classId - Class ID
   */
  async updateParticipantCountInFirestore(classId) {
    try {
      // Get current count from Realtime DB
      const participantsSnapshot = await new Promise((resolve, reject) => {
        const participantsRef = ref(realtimeDb, `liveClasses/${classId}/participants`);
        const timeout = setTimeout(() => {
          off(participantsRef);
          reject(new Error('Timeout'));
        }, 3000);

        onValue(participantsRef, (snapshot) => {
          clearTimeout(timeout);
          off(participantsRef);
          resolve(snapshot);
        }, (error) => {
          clearTimeout(timeout);
          off(participantsRef);
          reject(error);
        });
      });
      
      const participantsData = participantsSnapshot.val() || {};
      const participantCount = Object.keys(participantsData).length;
      
      console.log(`Updating participant count for ${classId}: ${participantCount} participants`);
      
      // Update Firestore
      const liveClassRef = doc(db, 'liveClasses', classId);
      await updateDoc(liveClassRef, {
        participantCount,
        updatedAt: new Date()
      });
      
      console.log(`Successfully updated participant count to ${participantCount}`);
    } catch (error) {
      console.error('Error updating participant count in Firestore:', error);
      // Don't throw - this is a background sync operation
    }
  }
};

